import {
    AiContentChannel,
    getMarkdownChartTypeFromOpenUi,
    normalizeOpenUiMetricTrend,
    OPENUI_REFERENCE_COMPONENT_NAMES,
    OpenUiComponentName,
    openUiQuestOverviewSummaryPropsSchema,
} from '../../types/aiContent';
import {
    type OpenUiChannelAdapter,
    type OpenUiContentNode,
    isOpenUiContentNode,
    translateOpenUiNodesForChannel,
    translateValidatedOpenUiTreeForChannel,
} from './openUiChannelTranslator';
import { isSafeInternalPath } from './safeInternalPath';

export type OpenUiMarkdownContext = {
    baseUrl?: string;
    projectUuid?: string;
};

export type OpenUiMarkdownTranslation = {
    markdown: string;
    isInvalid: boolean;
};

const referenceComponentNames = new Set<string>(
    OPENUI_REFERENCE_COMPONENT_NAMES,
);

const stringProp = (node: OpenUiContentNode, key: string): string => {
    const value = node.props[key];
    return typeof value === 'string' ? value : '';
};

const stringArrayProp = (node: OpenUiContentNode, key: string): string[] => {
    const value = node.props[key];
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is string => typeof item === 'string');
};

const unknownObjectProp = (value: object, key: string): unknown =>
    (value as Record<string, unknown>)[key];

const DANGEROUS_HTML_ELEMENTS =
    'script|style|iframe|object|embed|svg|form|button|select|textarea';

const stripUnsafeControlCharacters = (value: string): string =>
    Array.from(value)
        .filter((character) => {
            const codePoint = character.codePointAt(0);
            return (
                codePoint === undefined ||
                codePoint === 0x09 ||
                codePoint === 0x0a ||
                codePoint === 0x0d ||
                (codePoint >= 0x20 && codePoint !== 0x7f)
            );
        })
        .join('');

const stripUnsafeText = (value: string): string =>
    stripUnsafeControlCharacters(
        value
            .replace(
                new RegExp(
                    `<(${DANGEROUS_HTML_ELEMENTS})\\b[^>]*>[\\s\\S]*?<\\/\\1\\s*>`,
                    'gi',
                ),
                '',
            )
            .replace(
                new RegExp(
                    `<(?:${DANGEROUS_HTML_ELEMENTS}|input)\\b[^>]*\\/?\\s*>`,
                    'gi',
                ),
                '',
            )
            .replace(/!\[([^\]]*)\]\((?:[^()]|\([^()]*\))*\)/g, '$1')
            .replace(/!\[([^\]]*)\]\[[^\]]*\]/g, '$1')
            .replace(/\[([^\]]+)\]\((?:[^()]|\([^()]*\))*\)/g, '$1')
            .replace(/\[([^\]]+)\]\[[^\]]*\]/g, '$1')
            .replace(/^\s*\[[^\]]+\]:\s*\S+.*$/gm, '')
            .replace(/<\/?[A-Za-z][^>]*>/g, '')
            .replace(/(?:https?|javascript|data|vbscript):[^\s<>()]+/gi, ''),
    );

export const sanitizeOpenUiPlainTextForChannel = (value: string): string =>
    stripUnsafeText(value).replace(/([\\`*_[\]<>])/g, '\\$1');
const escapeMarkdownText: (value: string) => string =
    sanitizeOpenUiPlainTextForChannel;

const escapeTableCell = (value: string): string => {
    const escaped: string = escapeMarkdownText(value);
    return escaped.replace(/\|/g, '\\|').replace(/\n/g, ' ').trim();
};

const serializeTable = (headers: string[], rows: string[][]): string => {
    if (headers.length === 0) return '';

    const header = `| ${headers.map(escapeTableCell).join(' | ')} |`;
    const divider = `| ${headers.map(() => '---').join(' | ')} |`;
    const body = rows.map(
        (row) =>
            `| ${headers
                .map((_, index) => escapeTableCell(row[index] ?? ''))
                .join(' | ')} |`,
    );
    return [header, divider, ...body].join('\n');
};

const toAbsoluteSortmentUrl = (
    path: string,
    baseUrl?: string,
): string | null => {
    if (!isSafeInternalPath(path)) return null;
    if (!baseUrl) return path;
    return `${baseUrl.replace(/\/$/, '')}${path}`;
};

const escapeMarkdownLinkDestination = (url: string): string =>
    url.replace(
        /[[\]()<>'"\\]/g,
        (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`,
    );

const serializeSafeMarkdownLink = (label: string, url: string): string =>
    `[${label}](${escapeMarkdownLinkDestination(url)})`;

const sanitizeMarkdownSegment = (
    value: string,
    context: OpenUiMarkdownContext,
): string =>
    value
        .replace(/!\[([^\]]*)\]\((?:[^()]|\([^()]*\))*\)/g, '$1')
        .replace(
            /\[([^\]]+)\]\(((?:[^()]|\([^()]*\))*)\)/g,
            (_match, label: string, href: string) => {
                const safeUrl = toAbsoluteSortmentUrl(href, context.baseUrl);
                return safeUrl
                    ? serializeSafeMarkdownLink(stripUnsafeText(label), safeUrl)
                    : stripUnsafeText(label);
            },
        )
        .replace(/<\/?[A-Za-z][^>]*>/g, '');

const sanitizeUnfencedMarkdown = (
    value: string,
    context: OpenUiMarkdownContext,
): string => {
    const inlineCode: string[] = [];
    const safeLinks: string[] = [];
    const withProtectedInlineCode = value.replace(/`+[^`\n]*`+/g, (code) => {
        const token = `\uE000${inlineCode.length}\uE001`;
        inlineCode.push(code);
        return token;
    });
    const sanitized = withProtectedInlineCode
        .replace(
            new RegExp(
                `<(${DANGEROUS_HTML_ELEMENTS})\\b[^>]*>[\\s\\S]*?<\\/\\1\\s*>`,
                'gi',
            ),
            '',
        )
        .replace(
            new RegExp(
                `<(?:${DANGEROUS_HTML_ELEMENTS}|input)\\b[^>]*\\/?\\s*>`,
                'gi',
            ),
            '',
        )
        .replace(/!\[([^\]]*)\]\((?:[^()]|\([^()]*\))*\)/g, '$1')
        .replace(/!\[([^\]]*)\]\[[^\]]*\]/g, '$1')
        .replace(
            /\[([^\]]+)\]\(((?:[^()]|\([^()]*\))*)\)/g,
            (_match, label: string, href: string) => {
                const safeUrl = toAbsoluteSortmentUrl(
                    href.trim().split(/\s+/)[0],
                    context.baseUrl,
                );
                if (!safeUrl) return label;
                const token = `\uE100${safeLinks.length}\uE101`;
                safeLinks.push(
                    serializeSafeMarkdownLink(stripUnsafeText(label), safeUrl),
                );
                return token;
            },
        )
        .replace(/\[([^\]]+)\]\[[^\]]*\]/g, '$1')
        .replace(/^\s*\[[^\]]+\]:\s*\S+.*$/gm, '')
        .replace(/<((?:https?|javascript|data|vbscript):[^>]+)>/gi, '')
        .replace(/(?:https?|javascript|data|vbscript):[^\s<>()]+/gi, '')
        .split('\n')
        .map((line) => sanitizeMarkdownSegment(line, context))
        .join('\n');

    const withRestoredLinks = safeLinks.reduce(
        (result, link, index) => result.replace(`\uE100${index}\uE101`, link),
        sanitized,
    );

    return inlineCode.reduce(
        (result, code, index) => result.replace(`\uE000${index}\uE001`, code),
        withRestoredLinks,
    );
};

const normalizeCodeLanguage = (value: string): string => {
    const language = value.trim().split(/\s+/)[0] ?? '';
    return /^[A-Za-z0-9_+.-]{1,32}$/.test(language) ? language : '';
};

export const sanitizeOpenUiMarkdownForChannel = (
    value: string,
    context: OpenUiMarkdownContext,
): string => {
    let fence: { marker: string; length: number } | null = null;
    let proseLines: string[] = [];
    const output: string[] = [];
    const flushProse = () => {
        if (proseLines.length === 0) return;
        output.push(sanitizeUnfencedMarkdown(proseLines.join('\n'), context));
        proseLines = [];
    };

    value.split('\n').forEach((line) => {
        const fenceMatch = line.match(/^\s*(`{3,}|~{3,})(.*)$/);
        if (!fence) {
            if (!fenceMatch) {
                proseLines.push(line);
                return;
            }
            flushProse();
            fence = {
                marker: fenceMatch[1][0],
                length: fenceMatch[1].length,
            };
            const language = normalizeCodeLanguage(fenceMatch[2]);
            output.push(`${fenceMatch[1]}${language}`);
            return;
        }

        output.push(line);
        if (
            fenceMatch &&
            fence.marker === fenceMatch[1][0] &&
            fenceMatch[1].length >= fence.length &&
            fenceMatch[2].trim().length === 0
        ) {
            fence = null;
        }
    });
    flushProse();

    return output.join('\n');
};

const codeFence = (code: string, language = ''): string => {
    const longestFence = Math.max(
        2,
        ...Array.from(code.matchAll(/`+/g), (match) => match[0].length),
    );
    const fence = '`'.repeat(longestFence + 1);
    return `${fence}${normalizeCodeLanguage(language)}\n${code}\n${fence}`;
};

const serializeMetric = (node: OpenUiContentNode): string => {
    const label = escapeMarkdownText(stringProp(node, 'label'));
    const value = escapeMarkdownText(stringProp(node, 'value'));
    const change = escapeMarkdownText(stringProp(node, 'change'));
    const trend = normalizeOpenUiMetricTrend(stringProp(node, 'trend'));
    const trendLabel = trend ? `trend: ${trend.toLowerCase()}` : '';
    const detail = [change, trendLabel].filter(Boolean).join(', ');
    return detail
        ? `- **${label}:** **${value}** (${detail})`
        : `- **${label}:** **${value}**`;
};

const serializeDataTable = (
    node: OpenUiContentNode,
    _context: OpenUiMarkdownContext,
): string => {
    const headers = stringArrayProp(node, 'headers');
    const rawRows = node.props.rows;
    const rows = Array.isArray(rawRows)
        ? rawRows.map((row) =>
              Array.isArray(row) ? row.map((cell) => String(cell ?? '')) : [],
          )
        : [];
    const caption = stringProp(node, 'caption');
    return [
        caption ? `### ${escapeMarkdownText(caption)}` : '',
        serializeTable(headers, rows),
    ]
        .filter(Boolean)
        .join('\n\n');
};

const serializeChart = (
    node: OpenUiContentNode,
    _context: OpenUiMarkdownContext,
): string => {
    const title = escapeMarkdownText(stringProp(node, 'title'));
    const chartType = escapeMarkdownText(
        getMarkdownChartTypeFromOpenUi(stringProp(node, 'chartType')) ?? '',
    );
    const rawSlices = node.props.slices;
    if (Array.isArray(rawSlices)) {
        const rows = rawSlices.flatMap((slice) => {
            if (typeof slice !== 'object' || slice === null) return [];
            const name = unknownObjectProp(slice, 'name');
            const value = unknownObjectProp(slice, 'value');
            return typeof name === 'string' && typeof value === 'number'
                ? [[name, String(value)]]
                : [];
        });
        return [
            `### ${title}`,
            chartType ? `_Chart type: ${chartType}_` : '',
            serializeTable(['Category', 'Value'], rows),
        ]
            .filter(Boolean)
            .join('\n\n');
    }

    const xAxis = stringArrayProp(node, 'xAxis');
    const rawSeries = node.props.series;
    const series = Array.isArray(rawSeries)
        ? rawSeries.flatMap((item) => {
              if (typeof item !== 'object' || item === null) return [];
              const name = unknownObjectProp(item, 'name');
              const values = unknownObjectProp(item, 'values');
              if (typeof name !== 'string' || !Array.isArray(values)) return [];
              return [{ name, values: values.map(String) }];
          })
        : [];
    const rows = xAxis.map((category, categoryIndex) => [
        category,
        ...series.map((item) => item.values[categoryIndex] ?? ''),
    ]);
    return [
        `### ${title}`,
        chartType ? `_Chart type: ${chartType}_` : '',
        serializeTable(['Category', ...series.map((item) => item.name)], rows),
    ]
        .filter(Boolean)
        .join('\n\n');
};

const serializeClarification = (node: OpenUiContentNode): string => {
    const header = escapeMarkdownText(stringProp(node, 'header'));
    const rawQuestions = node.props.questions;
    const questions = Array.isArray(rawQuestions)
        ? rawQuestions.flatMap((question) => {
              if (typeof question !== 'object' || question === null) return [];
              const prompt = unknownObjectProp(question, 'question');
              const rawOptions = unknownObjectProp(question, 'options');
              if (typeof prompt !== 'string') return [];
              const options = Array.isArray(rawOptions)
                  ? rawOptions.flatMap((option) => {
                        if (typeof option !== 'object' || option === null)
                            return [];
                        const label = unknownObjectProp(option, 'label');
                        const recommended =
                            unknownObjectProp(option, 'recommended') === true;
                        const description = unknownObjectProp(
                            option,
                            'description',
                        );
                        return typeof label === 'string'
                            ? [
                                  [
                                      `- ${escapeMarkdownText(label)}${
                                          recommended ? ' _(Recommended)_' : ''
                                      }`,
                                      typeof description === 'string' &&
                                      description
                                          ? ` — ${escapeMarkdownText(
                                                description,
                                            )}`
                                          : '',
                                  ].join(''),
                              ]
                            : [];
                    })
                  : [];
              const allowsCustomAnswer =
                  unknownObjectProp(question, 'allowCustomAnswer') === true;
              return [
                  [
                      `**${escapeMarkdownText(prompt)}**`,
                      ...options,
                      ...(allowsCustomAnswer
                          ? ['- _A custom answer is also accepted._']
                          : []),
                  ].join('\n'),
              ];
          })
        : [];
    return [`### ${header}`, ...questions].filter(Boolean).join('\n\n');
};

const getReferenceTypeLabel = (node: OpenUiContentNode): string =>
    node.typeName.replace(/Reference$/, '').replace(/([a-z])([A-Z])/g, '$1 $2');

const serializeReference = (
    node: OpenUiContentNode,
    listItem: boolean,
): string => {
    const label = stringProp(node, 'label');
    if (!label) return '';

    const prefix = listItem ? '- ' : '';
    return `${prefix}**${escapeMarkdownText(
        getReferenceTypeLabel(node),
    )}:** ${escapeMarkdownText(label)}`;
};

const serializeReferenceLines = (
    node: OpenUiContentNode,
): string[] | undefined => {
    const { children } = node.props;
    if (!Array.isArray(children)) return undefined;

    const references = children.map((child) => {
        if (
            !isOpenUiContentNode(child) ||
            !referenceComponentNames.has(child.typeName)
        )
            return undefined;
        return serializeReference(child, true);
    });
    if (references.some((reference) => reference === undefined))
        return undefined;

    return references.filter((reference): reference is string =>
        Boolean(reference),
    );
};

const serializeReferences = (node: OpenUiContentNode): string | undefined => {
    const lines = serializeReferenceLines(node);
    return lines === undefined
        ? undefined
        : `## References\n\n${lines.join('\n')}`;
};

export const serializeOpenUiNodeToMarkdown = (
    node: OpenUiContentNode,
    context: OpenUiMarkdownContext,
): string | undefined => {
    switch (node.typeName) {
        case OpenUiComponentName.MARKDOWN:
            return sanitizeOpenUiMarkdownForChannel(
                stringProp(node, 'content'),
                context,
            );
        case OpenUiComponentName.METRIC:
            return serializeMetric(node);
        case OpenUiComponentName.CALLOUT: {
            const title = escapeMarkdownText(stringProp(node, 'title'));
            const body = sanitizeOpenUiMarkdownForChannel(
                stringProp(node, 'body'),
                context,
            );
            return [
                `> **${title}**`,
                ...body.split('\n').map((line) => `> ${line}`),
            ].join('\n');
        }
        case OpenUiComponentName.DATA_TABLE:
            return serializeDataTable(node, context);
        case OpenUiComponentName.CHART:
            return serializeChart(node, context);
        case OpenUiComponentName.SQL_RUNNER:
            return [
                `### ${escapeMarkdownText(stringProp(node, 'label'))}`,
                codeFence(stringProp(node, 'sql'), 'sql'),
            ]
                .filter(Boolean)
                .join('\n\n');
        case OpenUiComponentName.CLARIFICATION:
            return serializeClarification(node);
        case OpenUiComponentName.CONNECTION_PICKER:
            return `${escapeMarkdownText(stringProp(node, 'title'))}\n\nChoose a connection in Sortment chat.`;
        case OpenUiComponentName.ACCORDION:
            return [
                `### ${escapeMarkdownText(stringProp(node, 'title'))}`,
                sanitizeOpenUiMarkdownForChannel(
                    stringProp(node, 'content'),
                    context,
                ),
            ]
                .filter(Boolean)
                .join('\n\n');
        case OpenUiComponentName.CODE:
            return codeFence(
                stringProp(node, 'code'),
                stringProp(node, 'language'),
            );
        case OpenUiComponentName.PRODUCT_LINK: {
            const label = escapeMarkdownText(stringProp(node, 'label'));
            const url = toAbsoluteSortmentUrl(
                stringProp(node, 'path'),
                context.baseUrl,
            );
            return url ? serializeSafeMarkdownLink(label, url) : label;
        }
        case OpenUiComponentName.SCHEDULE_LINK: {
            const label = escapeMarkdownText(
                stringProp(node, 'label') || 'Sync schedules',
            );
            if (!context.projectUuid) return label;
            const url = toAbsoluteSortmentUrl(
                `/projects/${context.projectUuid}/settings/sync-schedule`,
                context.baseUrl,
            );
            return url ? serializeSafeMarkdownLink(label, url) : label;
        }
        case OpenUiComponentName.KPI_CARD: {
            const label = escapeMarkdownText(stringProp(node, 'label'));
            if (!context.projectUuid) return label;
            const url = toAbsoluteSortmentUrl(
                `/projects/${context.projectUuid}/kpi`,
                context.baseUrl,
            );
            return url ? serializeSafeMarkdownLink(label, url) : label;
        }
        case OpenUiComponentName.QUEST_OVERVIEW_SUMMARY: {
            const parsed = openUiQuestOverviewSummaryPropsSchema.safeParse(
                node.props,
            );
            if (!parsed.success) return undefined;
            if (parsed.data.contextMarkdown !== undefined) {
                return [
                    `## Objective\n\n${escapeMarkdownText(parsed.data.originalObjective)}`,
                    `## Context\n\n${sanitizeOpenUiMarkdownForChannel(parsed.data.contextMarkdown, context)}`,
                ].join('\n\n');
            }
            const sections = [
                `## Current state\n\n${sanitizeOpenUiMarkdownForChannel(
                    parsed.data.currentState,
                    context,
                )}`,
            ];
            if (parsed.data.kpis?.length) {
                const kpis = parsed.data.kpis.map((kpi) => {
                    const label = escapeMarkdownText(kpi.label);
                    if (!context.projectUuid) return `- ${label}`;
                    const url = toAbsoluteSortmentUrl(
                        `/projects/${context.projectUuid}/kpi`,
                        context.baseUrl,
                    );
                    return `- ${
                        url ? serializeSafeMarkdownLink(label, url) : label
                    }`;
                });
                sections.push(`## KPIs\n\n${kpis.join('\n')}`);
            }
            sections.push(
                `## Original objective\n\n${escapeMarkdownText(
                    parsed.data.originalObjective,
                )}`,
            );
            return sections.join('\n\n');
        }
        case OpenUiComponentName.REFERENCES:
            return serializeReferences(node);
        default:
            return referenceComponentNames.has(node.typeName)
                ? serializeReference(node, false)
                : undefined;
    }
};

type OpenUiMarkdownItem =
    | { kind: 'content'; markdown: string }
    | { kind: 'references'; lines: string[] };

const markdownAdapter: OpenUiChannelAdapter<
    OpenUiMarkdownItem,
    OpenUiMarkdownContext
> = {
    channel: AiContentChannel.MARKDOWN,
    renderComponent: (node, context) => {
        if (node.typeName === OpenUiComponentName.REFERENCES) {
            const lines = serializeReferenceLines(node);
            return lines === undefined
                ? undefined
                : [{ kind: 'references', lines }];
        }
        const markdown = serializeOpenUiNodeToMarkdown(node, context);
        return markdown === undefined
            ? undefined
            : [{ kind: 'content', markdown }];
    },
    renderMetricGroup: (nodes, context) => {
        const metrics = nodes.map((node) =>
            serializeOpenUiNodeToMarkdown(node, context),
        );
        return metrics.some((metric) => metric === undefined)
            ? undefined
            : [
                  {
                      kind: 'content',
                      markdown: metrics.filter(Boolean).join('\n'),
                  },
              ];
    },
};

const toMarkdownTranslation = (translation: {
    items: OpenUiMarkdownItem[];
    isInvalid: boolean;
}): OpenUiMarkdownTranslation => {
    if (translation.isInvalid) return { markdown: '', isInvalid: true };

    const content: string[] = [];
    const references: string[] = [];
    translation.items.forEach((item) => {
        if (item.kind === 'references') {
            references.push(...item.lines);
        } else if (item.markdown) {
            content.push(item.markdown);
        }
    });
    const uniqueReferences = [...new Set(references.filter(Boolean))];
    if (uniqueReferences.length > 0) {
        content.push(`## References\n\n${uniqueReferences.join('\n')}`);
    }

    return {
        markdown: content.join('\n\n'),
        isInvalid: false,
    };
};

export const translateValidatedOpenUiTreeToMarkdown = (args: {
    root: OpenUiContentNode;
    context?: OpenUiMarkdownContext;
}): OpenUiMarkdownTranslation =>
    toMarkdownTranslation(
        translateValidatedOpenUiTreeForChannel({
            root: args.root,
            context: args.context ?? {},
            adapter: markdownAdapter,
        }),
    );

export const translateValidatedOpenUiNodesToMarkdown = (args: {
    nodes: unknown[];
    context?: OpenUiMarkdownContext;
}): OpenUiMarkdownTranslation =>
    toMarkdownTranslation(
        translateOpenUiNodesForChannel({
            nodes: args.nodes,
            context: args.context ?? {},
            adapter: markdownAdapter,
        }),
    );
